/**
 * CoachCard — the instruction card of the tutorial spotlight system.
 *
 * Styled from DS2 tokens to read like the canonical black/gold dialogs, but
 * deliberately NOT a true modal while the player must interact with the
 * highlighted real element: no aria-modal, no focus trap, no inert — just a
 * labelled instructional region whose step text is announced politely.
 * (True modal semantics belong to the Welcome / chapter-transition /
 * completion dialogs, which use DS2Modal.)
 *
 * Placement: measured after render, positioned by the pure placeCoachCard
 * rule so it never covers a highlighted region; compact top/bottom bar on
 * mobile layouts.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { DS2 } from '../../theme/designTokens';
import { useTranslation } from '../../i18n/react';
import {
  placeCoachCard,
  type SpotRect,
} from '../../tutorial/spotlight/spotlightGeometry';
import { SPOTLIGHT_Z } from './SpotlightOverlay';

const CARD_STYLE = `
.tut-coach {
  position: fixed; z-index: ${SPOTLIGHT_Z + 3};
  width: min(340px, calc(100vw - 24px));
  background: ${DS2.color.surface};
  border: 1px solid rgba(201,168,76,0.35);
  border-radius: 16px;
  box-shadow: 0 18px 50px -18px rgba(0,0,0,0.85), 0 0 24px -8px rgba(201,168,76,0.25);
  padding: 14px 16px;
  transition: left 220ms ease, top 220ms ease, opacity 180ms ease;
}
.tut-coach--measuring { opacity: 0; pointer-events: none; }
.tut-coach-progress {
  font-family: ${DS2.font.small}; font-size: 10.5px; letter-spacing: 0.08em;
  text-transform: uppercase; color: ${DS2.color.gold}; margin-bottom: 4px;
}
.tut-coach-title {
  font-family: ${DS2.font.heading}; font-weight: 700; font-size: 15px;
  color: ${DS2.color.textBright}; margin: 0 0 4px;
}
.tut-coach-body {
  font-family: ${DS2.font.body}; font-size: 13px; line-height: 1.45;
  color: ${DS2.color.textBright}; opacity: 0.92; margin: 0;
}
.tut-coach-hint {
  font-family: ${DS2.font.body}; font-size: 12px; color: #ff9d9d; margin: 6px 0 0;
}
.tut-coach-actions {
  display: flex; align-items: center; gap: 8px; margin-top: 12px; flex-wrap: wrap;
}
.tut-coach-btn {
  font-family: ${DS2.font.body}; font-size: 13px; font-weight: 600;
  border-radius: 12px; padding: 7px 16px; cursor: pointer;
  border: 1px solid transparent;
}
.tut-coach-btn--gold { background: ${DS2.gradient.gold}; color: #0b0b0b; border: none; }
.tut-coach-btn--ghost { background: #131313; color: #FFFFFF; border: 1px solid ${DS2.color.gold}; }
.tut-coach-btn--text {
  background: transparent; border: none; color: ${DS2.color.textBright};
  opacity: 0.7; font-size: 12px; text-decoration: underline; padding: 7px 6px;
}
.tut-coach-btn--text:hover { opacity: 1; }
.tut-coach-spacer { flex: 1; }
@media (prefers-reduced-motion: reduce) { .tut-coach { transition: none; } }
`;

export interface CoachCardProps {
  /** e.g. "Chapter 1 · Step 3 of 10" */
  progress: string;
  title: string;
  body: string;
  /** Transient wrong-action hint (null = hidden). */
  hint?: string | null;
  /** Highlighted regions to avoid (viewport coords, padded or raw). */
  avoid: SpotRect[];
  isMobileLayout: boolean;
  onContinue?: () => void;
  continueLabel?: string;
  onBack?: () => void;
  onSkipChapter?: () => void;
  onExit: () => void;
}

export function CoachCard(props: CoachCardProps) {
  const { t } = useTranslation('tutorial');
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Two-pass: render invisible, measure, place. Re-place when the targets,
  // copy, or layout mode change.
  const avoidKey = JSON.stringify(props.avoid);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const b = el.getBoundingClientRect();
      const placed = placeCoachCard(
        props.avoid,
        { w: b.width, h: b.height },
        { w: window.innerWidth, h: window.innerHeight },
        props.isMobileLayout,
      );
      setPos({ left: placed.left, top: placed.top });
    };
    measure();
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avoidKey, props.title, props.body, props.isMobileLayout]);

  return (
    <div
      ref={ref}
      className={`tut-coach${pos ? '' : ' tut-coach--measuring'}`}
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0 }}
      role="group"
      aria-label={t('controls.stepAria')}
    >
      <style>{CARD_STYLE}</style>
      <div aria-live="polite">
        <div className="tut-coach-progress">{props.progress}</div>
        <h2 className="tut-coach-title">{props.title}</h2>
        <p className="tut-coach-body">{props.body}</p>
        {props.hint && <p className="tut-coach-hint">{props.hint}</p>}
      </div>
      <div className="tut-coach-actions">
        {props.onBack && (
          <button type="button" className="tut-coach-btn tut-coach-btn--ghost" onClick={props.onBack}>
            {t('controls.back')}
          </button>
        )}
        {props.onContinue && (
          <button type="button" className="tut-coach-btn tut-coach-btn--gold" onClick={props.onContinue}>
            {props.continueLabel ?? t('controls.continue')}
          </button>
        )}
        <span className="tut-coach-spacer" />
        {props.onSkipChapter && (
          <button type="button" className="tut-coach-btn tut-coach-btn--text" onClick={props.onSkipChapter}>
            {t('controls.skipChapter')}
          </button>
        )}
        <button type="button" className="tut-coach-btn tut-coach-btn--text" onClick={props.onExit}>
          {t('controls.exit')}
        </button>
      </div>
    </div>
  );
}
